import { Helmet } from 'react-helmet-async';
import { PublicNavbar } from '@/components/layout/PublicNavbar';
import { PublicFooter } from '@/components/layout/PublicFooter';
import { PageHero, SectionHeading } from '@/components/layout/PublicSections';
import { OptimizedImage } from '@/components/common/OptimizedImage';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { listBarbers } from '@/services/barberService';
import { branchService } from '@/services/branchService';
import type { Barber, Branch } from '@/types';
import { MapPin, Phone, Filter, Loader2, Store, Target } from 'lucide-react';
import { MONKEY_FRANCHISE_ID } from '@/lib/config';

export default function About() {
  const { t } = useTranslation();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [barbersByBranch, setBarbersByBranch] = useState<Record<string, Barber[]>>({});
  const [loading, setLoading] = useState(true);
  const [selectedBranchId, setSelectedBranchId] = useState<string>('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch all branches
        const allBranches = await branchService.list();

        // Filter only Monkey Barber Club branches
        const monkeyBranches = allBranches.filter(
          (branch) => branch.franchiseId === MONKEY_FRANCHISE_ID
        );
        setBranches(monkeyBranches);

        // Fetch all barbers
        const barbersData = await listBarbers();

        // Group barbers by branch (only Monkey branches)
        const grouped: Record<string, Barber[]> = {};
        barbersData.forEach((barber) => {
          // Only include barbers from Monkey branches
          const isMonkeyBranch = monkeyBranches.some(b => b.branchId === barber.branchId);
          if (isMonkeyBranch) {
            if (!grouped[barber.branchId]) {
              grouped[barber.branchId] = [];
            }
            grouped[barber.branchId].push(barber);
          }
        });

        setBarbersByBranch(grouped);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Get initials from barber name
  const getInitials = (name: string) => {
    const words = name.trim().split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Filter branches based on selection
  const filteredBranches = selectedBranchId === 'all'
    ? branches
    : branches.filter(branch => branch.branchId === selectedBranchId);

  // Leandro is the founder & head barber — featured at the top, and excluded
  // from the per-branch team grids so he isn't shown twice.
  const isFounder = (barber: Barber) => /leandro/i.test(barber.displayName);
  const founder = Object.values(barbersByBranch).flat().find(isFounder) || null;

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <Helmet>
        <title>{t('about.seo.title')}</title>
      </Helmet>
      <PublicNavbar />

      <PageHero
        eyebrow={t('about.hero.tagline')}
        title={t('about.hero.title')}
      />

      {/* Intro */}
      <section className="border-b border-hairline bg-surface py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid items-center gap-12 md:grid-cols-2 md:gap-16">
            <div>
              <OptimizedImage
                src="/logo_w_350.png"
                alt="Monkey Barber Logo"
                className="h-20 w-auto md:h-24"
              />
              <h2 className="mt-8 font-sans text-2xl font-semibold leading-snug tracking-tight text-ink md:text-3xl">
                {t('about.intro.heading')}{' '}
                <span className="text-accent">{t('about.intro.headingHighlight')}</span>
              </h2>
              <p className="mt-6 text-base leading-relaxed text-ink-muted md:text-lg">
                {t('about.intro.description')}
              </p>
            </div>

            <div className="rounded-xl border border-hairline bg-canvas p-8">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-hairline bg-surface-2 text-ink-subtle">
                <Target className="h-5 w-5" aria-hidden="true" />
              </span>
              <h3 className="mt-5 font-sans text-xl font-semibold tracking-tight text-ink">
                {t('about.mission.title')}
              </h3>
              <p className="mt-3 leading-relaxed text-ink-muted">{t('about.mission.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Branches & team */}
      <section className="bg-canvas py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-6">
          {/* Founder spotlight — Leandro, top & centered */}
          {!loading && (
            <div className="mb-20 md:mb-28">
              <SectionHeading
                eyebrow={t('about.founder.eyebrow')}
                title={t('about.founder.title')}
              />
              <div className="mx-auto mt-12 max-w-2xl rounded-xl border border-hairline bg-surface p-8 text-center md:p-10">
                <div className="mx-auto h-32 w-32 overflow-hidden rounded-full border border-hairline">
                  {founder?.photoURL ? (
                    <OptimizedImage
                      src={founder.photoURL}
                      alt={founder.displayName}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-surface-3">
                      <span className="font-sans text-4xl font-semibold text-ink-muted">L</span>
                    </div>
                  )}
                </div>
                <p className="mt-6 text-xs font-medium uppercase tracking-[0.22em] text-accent">
                  {t('about.founder.role')}
                </p>
                <h3 className="mt-2 font-sans text-2xl font-semibold tracking-tight text-ink md:text-3xl">
                  Leandro
                </h3>
                <p className="mx-auto mt-5 max-w-xl leading-relaxed text-ink-muted">
                  {t('about.founder.bio')}
                </p>
              </div>
            </div>
          )}

          <SectionHeading
            eyebrow={t('about.branches.subtitle')}
            title={t('about.branches.title')}
          />

          {/* Branch filter */}
          {!loading && branches.length > 1 && (
            <div className="mt-10 flex justify-center">
              <div className="w-full max-w-sm">
                <label
                  htmlFor="branch-filter"
                  className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-ink-subtle"
                >
                  <Filter className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('about.branches.filter.label')}
                </label>
                <select
                  id="branch-filter"
                  value={selectedBranchId}
                  onChange={(e) => setSelectedBranchId(e.target.value)}
                  className="w-full rounded-lg border border-hairline bg-surface-3 px-4 py-2.5 font-medium text-ink outline-none focus:border-transparent focus:ring-2 focus:ring-focus"
                >
                  <option value="all">{t('about.branches.filter.all')}</option>
                  {branches.map((branch) => (
                    <option key={branch.branchId} value={branch.branchId}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex flex-col items-center py-16 text-ink-subtle">
              <Loader2 className="h-8 w-8 animate-spin" aria-hidden="true" />
              <p className="mt-4">{t('about.branches.loading')}</p>
            </div>
          ) : branches.length === 0 ? (
            <div className="flex flex-col items-center py-16 text-ink-subtle">
              <Store className="h-8 w-8" aria-hidden="true" />
              <p className="mt-4">{t('about.branches.empty')}</p>
            </div>
          ) : (
            <div className="mt-14 space-y-12">
              {filteredBranches.map((branch) => {
                const branchBarbers = (barbersByBranch[branch.branchId] || []).filter(b => !isFounder(b));

                return (
                  <div
                    key={branch.branchId}
                    className="rounded-xl border border-hairline bg-surface p-6 md:p-8"
                  >
                    {/* Branch info */}
                    <div className="flex flex-col gap-6 border-b border-hairline pb-8 md:flex-row md:items-start md:justify-between">
                      <div className="flex-1">
                        <h3 className="font-sans text-2xl font-semibold tracking-tight text-ink">{branch.name}</h3>
                        <div className="mt-4 space-y-3 text-ink-muted">
                          <div className="flex items-start gap-3">
                            <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-ink-subtle" aria-hidden="true" />
                            <div>
                              <p>{branch.address}</p>
                              <p>{branch.city}, {branch.province} {branch.postalCode}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Phone className="h-5 w-5 shrink-0 text-ink-subtle" aria-hidden="true" />
                            <a href={`tel:${branch.phone}`} className="transition-colors hover:text-accent">
                              {branch.phone}
                            </a>
                          </div>
                        </div>
                      </div>

                      {branch.photo && (
                        <div className="h-48 w-full overflow-hidden rounded-xl border border-hairline md:w-64">
                          <OptimizedImage
                            src={branch.photo}
                            alt={branch.name}
                            className="h-full w-full object-cover"
                          />
                        </div>
                      )}
                    </div>

                    {/* Team */}
                    <div className="pt-8">
                      <h4 className="font-sans text-lg font-semibold tracking-tight text-ink">
                        {t('about.team.title')}
                      </h4>

                      {branchBarbers.length === 0 ? (
                        <p className="mt-6 text-ink-muted">{t('about.team.empty')}</p>
                      ) : (
                        <div className="mt-6 flex flex-wrap justify-center gap-6">
                          {branchBarbers.map((barber) => (
                            <div
                              key={barber.barberId}
                              className="w-full rounded-xl border border-hairline bg-surface-2 p-6 text-center transition-colors hover:border-hairline-strong sm:w-72"
                            >
                              <div className="mx-auto h-28 w-28 overflow-hidden rounded-full border border-hairline">
                                {barber.photoURL ? (
                                  <OptimizedImage
                                    src={barber.photoURL}
                                    alt={barber.displayName}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <div className="flex h-full w-full items-center justify-center bg-surface-3">
                                    <span className="font-sans text-3xl font-semibold text-ink-muted">
                                      {getInitials(barber.displayName)}
                                    </span>
                                  </div>
                                )}
                              </div>

                              <h5 className="mt-5 font-sans text-base font-semibold tracking-tight text-ink">
                                {barber.displayName}
                              </h5>
                              <p className="mt-1 text-sm text-ink-subtle">{t('about.team.barberRole')}</p>

                              {barber.specialties && barber.specialties.length > 0 && (
                                <div className="mt-4 flex flex-wrap justify-center gap-2">
                                  {barber.specialties.map((specialty, index) => (
                                    <span
                                      key={index}
                                      className="rounded-full border border-hairline bg-surface-3 px-3 py-1 text-xs text-ink-muted"
                                    >
                                      {specialty}
                                    </span>
                                  ))}
                                </div>
                              )}

                              {barber.bio && (
                                <p className="mt-4 text-sm leading-relaxed text-ink-muted">{barber.bio}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
